/**
 * A grid of cells becomes a dataset: named columns, one decided type each, and
 * rows coerced to those types.
 *
 * This is where the notices come from, and they are written for the person who
 * uploaded the file rather than for whoever debugs it. "We used the first row as
 * your column headings" is something a user can disagree with; "headerRow=true"
 * is not.
 */
import { blank, coerce, inferColumn, looksLikeHeaderRow, writtenDateFormat } from "./infer.js";
import { identifier, placeholderLabel, tidyLabel, unique } from "./names.js";
import { DEFAULT_MAX_COLUMNS, INFERENCE_SAMPLE, type Dataset, type Notice, type SourceColumn } from "./types.js";

export type Cell = string | number | boolean | Date | null;

export interface TableOptions {
  label: string;
  maxColumns?: number;
  dayFirst?: boolean;
  /** Overrides header detection when the user has told us. */
  headerRow?: boolean;
  /** True when the reader stopped at a row cap, so the notice can say so. */
  truncated?: boolean;
  /**
   * Data rows kept, counted *after* the heading row is decided.
   *
   * A reader cannot count this itself, because it stops before anybody knows
   * whether the first row is headings. Counted there, the heading row used a row of
   * the plan: Pro kept 49,999 rows and a file of exactly fifty thousand was told it
   * was too large. So the readers are asked for one row more and the cap is applied
   * here.
   */
  maxRows?: number;
  /**
   * Print dates the way the table wrote them, rather than in the house style.
   *
   * For a table out of a finished document, whose dates are already how its author
   * wanted them read. A spreadsheet's are not — they are ISO as often as not — so
   * this is off unless a caller says otherwise.
   */
  writtenDates?: boolean;
}

export function tableToDataset(grid: Cell[][], opts: TableOptions): Dataset {
  const notices: Notice[] = [];
  const rowsIn = grid.filter((r) => r.some((c) => !blank(c)));

  if (!rowsIn.length) {
    return { label: opts.label, columns: [], rows: [], headerRow: false, notices: [{ level: "warn", message: "There was nothing in this sheet." }] };
  }

  const headerRow = opts.headerRow ?? looksLikeHeaderRow(rowsIn);
  const headerCells = headerRow ? rowsIn[0]! : [];
  let body = headerRow ? rowsIn.slice(1) : rowsIn;
  let truncated = opts.truncated ?? false;
  if (opts.maxRows !== undefined && body.length > opts.maxRows) {
    body = body.slice(0, opts.maxRows);
    truncated = true;
  }

  if (!body.length) {
    return {
      label: opts.label,
      columns: [],
      rows: [],
      headerRow,
      notices: [{ level: "warn", message: "This file has headings but no rows underneath them." }]
    };
  }

  if (!headerRow) {
    notices.push({
      level: "ask",
      message: "The first row of this file looks like data rather than headings, so the columns have been numbered. If the first row is really your headings, say so and they will be used.",
      action: "confirm-header-row"
    });
  }

  // The width is the widest row, not the header's width: a ragged file where
  // one row has an extra value would otherwise lose it silently.
  const maxColumns = opts.maxColumns ?? DEFAULT_MAX_COLUMNS;
  const width = Math.min(Math.max(headerCells.length, ...body.map((r) => r.length)), maxColumns);
  const dropped = Math.max(headerCells.length, ...body.map((r) => r.length)) - width;
  if (dropped > 0) {
    notices.push({
      level: "warn",
      message: `This file has more than ${maxColumns} columns. The first ${maxColumns} have been kept; the rest are not available to a document.`
    });
  }

  // Inference reads a bounded sample. More than a couple of thousand rows buys
  // no better answer and costs the user a visible wait on a large workbook.
  const sample = body.slice(0, INFERENCE_SAMPLE);

  const headers: string[] = [];
  const labels: string[] = [];
  for (let c = 0; c < width; c++) {
    const raw = headerRow ? String(headerCells[c] ?? "").trim() : "";
    headers.push(raw);
    labels.push(raw ? tidyLabel(raw) : placeholderLabel(c));
  }
  const names = unique(headers.map((h, c) => identifier(h) || `column${c + 1}`));

  const columns: SourceColumn[] = [];
  for (let c = 0; c < width; c++) {
    const values = sample.map((r) => r[c] ?? null);
    const vote = inferColumn(headers[c] || labels[c]!, values, { dayFirst: opts.dayFirst !== false });
    const column: SourceColumn = {
      header: headers[c] || labels[c]!,
      name: names[c]!,
      label: labels[c]!,
      type: vote.type,
      samples: vote.samples,
      confidence: vote.confidence,
      include: true,
      constant: vote.constant,
      blankRatio: vote.blankRatio
    };
    if (vote.format) column.format = vote.format;
    if (vote.currency) column.currency = vote.currency;
    if (opts.writtenDates && vote.type === "date") {
      const written = writtenDateFormat(
        values.filter((v): v is string => typeof v === "string"),
        opts.dayFirst !== false
      );
      if (written) column.format = written;
    }
    columns.push(column);

    if (vote.ambiguousDateOrder) {
      notices.push({
        level: "ask",
        field: column.name,
        message: `Dates in “${column.label}” could be written day first or month first — every date in the column would make sense either way. Check that ${describeFirstDate(values)} is the date you meant.`,
        action: "confirm-date-order"
      });
    }
    if (vote.blankRatio > 0.5 && vote.blankRatio < 1) {
      notices.push({
        level: "info",
        field: column.name,
        message: `“${column.label}” is empty in more than half the rows. Documents using it will show a blank where there is no value.`
      });
    }
    if (vote.blankRatio === 1) {
      notices.push({ level: "info", field: column.name, message: `“${column.label}” is empty in every row. It has been left out; turn it on if you plan to fill it in later.` });
      column.include = false;
    }
    if (vote.confidence < 0.95 && vote.type !== "string") {
      notices.push({
        level: "ask",
        field: column.name,
        message: `Some values in “${column.label}” do not look like the rest. It is being treated as ${plain(column)}; change that if it is wrong.`,
        action: "check-type"
      });
    }
  }

  // Every column is coerced, including ones the user has switched off, because
  // switching one back on must not mean re-reading the file.
  const rows = body.map((r) => {
    const out: Record<string, unknown> = {};
    for (let c = 0; c < width; c++) out[columns[c]!.name] = coerce(r[c] ?? null, voteOf(columns[c]!), { dayFirst: opts.dayFirst !== false });
    return out;
  });

  if (truncated) {
    notices.push({
      level: "warn",
      message: `This file is larger than BroadPaper reads in one go. The first ${rows.length.toLocaleString("en-GB")} rows have been brought in.`
    });
  }

  return { label: opts.label, columns, rows, headerRow, notices };
}

/**
 * Re-applies a user's corrections to rows that are already parsed.
 *
 * Changing a column's type on the review screen must not mean re-reading the
 * file — the file may be a 40 MB workbook the browser has already let go of, and
 * a round trip for a dropdown is the kind of thing that makes a product feel
 * slow. The original strings are gone by this point, so the coercion runs
 * against whatever the previous pass produced, which is lossless for every
 * direction that matters: a number back to text, text to a number, a date to
 * text.
 */
export function recoerce(dataset: Dataset, columns: SourceColumn[], opts: { dayFirst?: boolean } = {}): Dataset {
  const changed = columns.filter((c) => {
    const was = dataset.columns.find((o) => o.name === c.name);
    return !was || was.type !== c.type || was.format !== c.format || was.currency !== c.currency;
  });
  if (!changed.length) return { ...dataset, columns };
  const rows = dataset.rows.map((row) => {
    const out = { ...row };
    for (const c of changed) out[c.name] = coerce(row[c.name], voteOf(c), opts);
    return out;
  });
  return { ...dataset, columns, rows };
}

function voteOf(column: SourceColumn) {
  return {
    type: column.type,
    format: column.format,
    currency: column.currency,
    confidence: column.confidence,
    constant: column.constant,
    blankRatio: column.blankRatio,
    samples: column.samples
  };
}

/** What a column's type is called in front of a user. Never "FieldType". */
export function plain(column: SourceColumn): string {
  switch (column.type) {
    case "currency":
      return column.currency ? `money in ${column.currency}` : "money";
    case "percent":
      return "a percentage";
    case "number":
      return "a number";
    case "date":
      return "a date";
    case "datetime":
      return "a date and time";
    case "boolean":
      return "yes or no";
    case "url":
      return "a web address";
    case "image":
      return "an image";
    default:
      return "text";
  }
}

function describeFirstDate(values: Cell[]): string {
  const first = values.find((v) => !blank(v));
  return first === undefined ? "the first date" : `“${String(first)}”`;
}
